import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { FileSignature, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { API } from "../config/api";

export default function SignAgreementPage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jotformUrl, setJotformUrl] = useState(null);
  const [requestInfo, setRequestInfo] = useState(null);

  useEffect(() => {
    const fetchJotFormUrl = async () => {
      if (!requestId) { setError("No request ID provided"); setLoading(false); return; }
      try {
        const [urlRes, reqRes] = await Promise.all([
          axios.get(`${API}/pole-requests/${requestId}/jotform-url`),
          axios.get(`${API}/request/${requestId}`).catch(() => null)
        ]);
        setJotformUrl(urlRes.data.url);
        if (reqRes?.data?.request) setRequestInfo(reqRes.data.request);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load agreement link");
      } finally {
        setLoading(false);
      }
    };
    fetchJotFormUrl();
  }, [requestId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileSignature className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Sign Rental Agreement</h2>
          {requestInfo && (
            <p className="text-slate-500 text-sm mb-2">
              Request #{requestId} - {requestInfo.user_name}
            </p>
          )}
          <p className="text-slate-600 mb-6">
            Click below to open and sign your rental agreement form.
          </p>

          <Button 
            onClick={() => window.open(jotformUrl, '_blank')} 
            size="lg" 
            className="w-full"
          >
            <FileSignature className="w-4 h-4 mr-2" />
            Open Rental Agreement
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-slate-400 mt-4">Opens in a new tab. Complete the form and you're all set!</p>
        </CardContent>
      </Card>
    </div>
  );
}
